import { ChangeEvent, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import Button from '@carrier-io/air-react/Button';
import FormControl from '@carrier-io/air-react/FormControl';
import InputLabel from '@carrier-io/air-react/InputLabel';
import Select from '@carrier-io/air-react/Select';
import Input from '@carrier-io/air-react/Input';
import MenuItem from '@carrier-io/air-react/MenuItem';
import FormHelperText from '@carrier-io/air-react/FormHelperText';
import Stack from '@carrier-io/air-react/Stack';
import {
  ClearButtonEndAdornment,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@carrier-io/air-react';
import SearchBox from '@carrier-io/air-react/patterns/SearchBox';
import Table from '@carrier-io/air-react/Table';
import ButtonBase from '@carrier-io/air-react/ButtonBase';

import { VendorPopup } from 'components/popups/VendorPopup';
import { Loader } from 'components/Loader';
import { StatBox } from 'components/StatBox';
import { BaseLayout } from 'layouts/BaseLayout';
import { PrivateRoute } from 'constants/routes';
import { TypographyColor, Variant } from 'constants/componentEnums';
import { useAuth } from 'context/AuthContext';
import { AllVendorsStats } from 'store/models/Stats';
import { isProjectExpired, isProjectFailed, isProjectOnboarding, ProjectOnboardingStatus } from 'store/models/Project';
import { Vendor } from '../../store/models/models';
import { UserRoles } from 'store/models/User';
import { GET_ALL_VENDORS } from '../../store/graphql/queries/GET_ALL_VENDORS';
import { useHandleError } from 'hooks/useHandleError';

import styles from '../../styles/Dashboard.module.css';

const ALL_VENDORS = 'All Vendors';

const MemberDashboard = () => {
  const [rawVendorsList, setRawVendorsList] = useState<Array<Vendor>>([]);
  const [vendorsList, setVendorsList] = useState<Array<Vendor>>([]);
  const [vendorsFilter, setVendorsFilter] = useState<Array<string>>([]);
  const [vendorsSearch, setVendorsSearch] = useState<string>('');
  const [vendorsStats, setVendorsStats] = useState<AllVendorsStats>({
    connectedDevices: 0,
    expCertificates: 0,
    failedDevices: 0,
    inprogessDevices: 0,
  });

  const [currPage, setCurrPage] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(5);
  const [vendorPopupOpened, setVendorPopupOpened] = useState(false);

  const navigate = useNavigate();
  const { currentUserRoles } = useAuth();
  const isAdmin = currentUserRoles?.includes(UserRoles.ADMIN);

  const { loading, data, error, refetch } = useQuery(GET_ALL_VENDORS, {
    fetchPolicy: 'network-only', // Used for first execution
    nextFetchPolicy: 'cache-first', // Used for subsequent executions
  });

  const goToProject = ({ vendorId }: Vendor) => {
    navigate(PrivateRoute.MemberVendorProjects.replace(':vendorId', vendorId));
  };

  const handleChange = (event: { target: { value: unknown } }) => {
    const { value } = event.target;
    const filter = typeof value === 'string' ? value.split(',') : value;

    setVendorsFilter(filter as string[]);
    setVendorsSearch('');
  };

  const handleVendorAdded = () => {
    refetch(); // This will re-run the GET_VENDORS query.
    setVendorPopupOpened(false);
  };

  const vendorPopupCancel = () => {
    setVendorPopupOpened(false);
  };

  const handleRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setCurrPage(0);
  };

  useEffect(() => {
    if (!loading && data?.getAllVendors) {
      setRawVendorsList(data.getAllVendors ?? []);
      setVendorsList(data.getAllVendors ?? []);
    }
  }, [data]);

  const filteredVendors = useMemo(() => {
    const filtered =
      vendorsFilter.length > 0 && !vendorsFilter.includes(ALL_VENDORS)
        ? rawVendorsList.filter((vendor) => vendorsFilter.includes(vendor.vendorName))
        : rawVendorsList;

    return vendorsSearch.length > 0
      ? filtered.filter((vendor) => vendor.vendorName.toLowerCase().includes(vendorsSearch.toLowerCase()))
      : filtered;
  }, [vendorsFilter, vendorsSearch, rawVendorsList]);

  useEffect(() => {
    setVendorsList(filteredVendors);

    if (currPage * itemsPerPage >= filteredVendors.length) {
      setCurrPage(0);
    }
  }, [filteredVendors, currPage, itemsPerPage]);

  const paginatedList = useMemo(() => {
    return vendorsList.slice(currPage * itemsPerPage, (currPage + 1) * itemsPerPage);
  }, [vendorsList, currPage, itemsPerPage]);

  useEffect(() => {
    const allConnectedDevices = vendorsList.reduce(
      (sum, vendor) =>
        sum + (vendor?.projects?.filter((pr) => pr.status === ProjectOnboardingStatus.TESTED)?.length ?? 0),
      0,
    );

    const failedDevices = vendorsList.reduce(
      (sum, vendor) => sum + (vendor?.projects?.filter((pr) => isProjectFailed(pr.status))?.length ?? 0),
      0,
    );

    const onboardingDevices = vendorsList.reduce(
      (sum, vendor) => sum + (vendor?.projects?.filter((project) => isProjectOnboarding(project.status))?.length ?? 0),
      0,
    );

    const expDevices = vendorsList.reduce(
      (sum, vendor) =>
        sum +
        (vendor?.projects?.filter(
          (project) =>
            project?.certificate && isProjectExpired(parseInt(project?.certificate?.expirationTime?.notAfter ?? '0')),
        )?.length ?? 0),
      0,
    );

    setVendorsStats({
      connectedDevices: allConnectedDevices,
      expCertificates: expDevices,
      inprogessDevices: onboardingDevices,
      failedDevices: failedDevices,
    });
  }, [vendorsList]);

  useHandleError(error);

  return (
    <BaseLayout>
      <Stack direction="column" justifyContent="flex-start" className={styles.container}>
        {!loading ? (
          <>
            <Stack id="header" direction="column" style={{ marginLeft: '20px' }}>
              <Stack direction="row" justifyContent="space-between" style={{ marginRight: '20px' }}>
                <Typography data-testid="page-title" variant={Variant.H4}>
                  Vendor Management
                </Typography>
                <div data-testid="select-vendor-view" style={{ width: '316px' }}>
                  <FormControl
                    sx={{
                      m: 1,
                      maxWidth: 300,
                      width: '100%',
                    }}
                    variant="filled"
                  >
                    <InputLabel id="multiple" style={{ width: '100%', textAlign: 'left' }}>
                      Select a vendor to view:
                    </InputLabel>
                    <Select
                      ref={{
                        current: '[Circular]',
                      }}
                      hideBackgroundColor
                      input={
                        <Input
                          endAdornment={
                            <ClearButtonEndAdornment
                              handleClear={() => setVendorsFilter([])}
                              position="end"
                              sx={{ marginTop: '0px !important' }}
                            />
                          }
                          sx={{ minWidth: 200, width: '100%' }}
                        />
                      }
                      label="Select a vendor to view:"
                      labelId="multiple"
                      multiple
                      onChange={handleChange}
                      showBorder
                      sx={{
                        marginRight: 5,
                        minWidth: 200,
                        width: '100%',
                        '.MuiFilledInput-input[aria-labelledby="multiple"]': {
                          textAlign: 'left',
                        },
                      }}
                      value={vendorsFilter}
                    >
                      <MenuItem value={ALL_VENDORS}>{ALL_VENDORS}</MenuItem>
                      {rawVendorsList.map((vendor) => (
                        <MenuItem key={vendor.vendorId} value={vendor.vendorName}>
                          {vendor.vendorName}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText />
                  </FormControl>
                </div>
              </Stack>
              <Stack data-testid="vendor-stats" direction="row" justifyContent="flex-start" spacing={3}>
                <StatBox value={vendorsStats.connectedDevices} title="Data Producers Connected" accumulation="total" />
                <StatBox value={vendorsStats.inprogessDevices} title="Onboarding in process" accumulation="total" />
                <StatBox value={vendorsStats.failedDevices} title="Onboarding failed" accumulation="total" />
                <StatBox
                  value={vendorsStats.expCertificates}
                  title="Certificates expiring"
                  accumulation="next 60 days"
                />
              </Stack>
            </Stack>
            <Stack id="body" direction="column" className={styles.pageBody}>
              {rawVendorsList.length > 0 ? (
                <Stack direction="column" style={{ marginLeft: '20px', marginRight: '25px' }}>
                  <Stack
                    data-testid="vendor-table-actions"
                    direction="row"
                    justifyContent="space-between"
                    style={{ marginTop: '15px' }}
                  >
                    <SearchBox
                      multiple={false}
                      onChange={(event) => setVendorsSearch(event.text)}
                      sx={{
                        maxWidth: '382px',
                        width: '100%',
                        backgroundColor: '#F1F3F5',
                      }}
                    />
                    {isAdmin && (
                      <Button data-testid="add-new-vendor" onClick={() => setVendorPopupOpened(true)}>
                        + Add New Vendor
                      </Button>
                    )}
                  </Stack>
                  <Stack id="vendorsGrid" direction="row">
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell align="left">
                              <Typography variant={Variant.TableHeader2}>Vendor</Typography>
                            </TableCell>
                            <TableCell align="left">
                              <Typography variant={Variant.TableHeader2}>Contact</Typography>
                            </TableCell>
                            <TableCell align="left">
                              <Typography variant={Variant.TableHeader2}>Email</Typography>
                            </TableCell>
                            <TableCell align="left">
                              <Typography variant={Variant.TableHeader2}>Phone number</Typography>
                            </TableCell>
                            <TableCell align="left">
                              <Typography variant={Variant.TableHeader2}>Data Producers Connected</Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedList.map((vendor) => (
                            <TableRow key={vendor.vendorId}>
                              <TableCell>
                                <ButtonBase onClick={() => goToProject(vendor)}>
                                  <Typography
                                    color={TypographyColor.Primary.Main}
                                    sx={{
                                      textDecoration: 'underline',
                                    }}
                                  >
                                    {vendor.vendorName}
                                  </Typography>
                                </ButtonBase>
                              </TableCell>
                              <TableCell>
                                <Typography>{`${vendor.firstName} ${vendor.lastName}`}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography>{vendor.email ?? '-'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography>{vendor.phone ?? '-'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography>{vendor?.projects?.length}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              data-testid="table-pagination"
                              count={vendorsList.length}
                              onPageChange={(_, page) => setCurrPage(page)}
                              onRowsPerPageChange={handleRowsPerPageChange}
                              page={currPage}
                              rowsPerPage={itemsPerPage}
                              rowsPerPageOptions={[1, 5, 10, 25]}
                              sx={{ width: '1000px' }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Stack>
                </Stack>
              ) : (
                <Stack
                  data-testid="vendors-empty"
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  spacing={3}
                  style={{
                    borderRadius: '4px',
                    backgroundColor: '#F6F8FA',
                    border: 'solid 1px #E0E0E0',
                    height: '226px',
                  }}
                >
                  <Typography>You don't have any vendors.</Typography>
                  {isAdmin && (
                    <Button data-testid="empty-add-new-vendor" onClick={() => setVendorPopupOpened(true)}>
                      + Add New Vendor
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          </>
        ) : (
          <Loader />
        )}
      </Stack>
      <VendorPopup
        onVendorAdded={handleVendorAdded}
        onCancel={vendorPopupCancel}
        opened={vendorPopupOpened}
        editableVendor={null}
        isEditing={false}
      />
    </BaseLayout>
  );
};

export default MemberDashboard;
